import { Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import React from "react";
import {
  CRITERIA_CHECK_KEYS,
  CRITERIA_CHECK_LABELS,
  getQuestionTone,
} from "./exam-feedback-pdf";
import MathRichText from "./math-rich-text";

const TONE_STYLES = {
  correct: {
    bar: "#D4F0E1",
    accent: "#1F9D73",
    score: "#148F77",
  },
  partial: {
    bar: "#FDE6C4",
    accent: "#E67E22",
    score: "#D35400",
  },
  incorrect: {
    bar: "#F8D7D7",
    accent: "#C0392B",
    score: "#C0392B",
  },
};

function CriteriaGrid({ checks }) {
  return (
    <Box mt={2}>
      <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#7B8790" mb={1}>
        CRITERIA CHECKS
      </Text>
      <SimpleGrid columns={2} spacing={1.5}>
        {CRITERIA_CHECK_KEYS.map((key) => {
          const raw = Boolean(checks?.[key]);
          const inverted = key === "answer_is_blank";
          const ok = inverted ? !raw : raw;
          const label =
            key === "answer_is_blank"
              ? ok
                ? "Answer provided"
                : "Answer is blank"
              : CRITERIA_CHECK_LABELS[key];
          return (
            <Flex
              key={key}
              align="center"
              px={2}
              py={1}
              bg={ok ? "#D4F0E1" : "#F8D7D7"}
              borderRadius="sm"
              gap={1}
            >
              <Text fontSize="10px" fontWeight="800" color={ok ? "#148F77" : "#C0392B"} lineHeight="1.3">
                {ok ? "OK" : "X"} {label}
              </Text>
            </Flex>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

function hasCriteriaPayload(checks) {
  if (!checks || typeof checks !== "object") return false;
  return CRITERIA_CHECK_KEYS.some((key) => typeof checks[key] === "boolean");
}

export function FeedbackQuestionCard({ question }) {
  const tone = getQuestionTone(question);
  const colors = TONE_STYLES[tone];
  const awarded = Number.isFinite(Number(question.marks_awarded))
    ? question.marks_awarded
    : "-";
  const max = Number.isFinite(Number(question.max_marks)) ? question.max_marks : "-";

  return (
    <Box mb={3}>
      <Flex
        align="center"
        bg={colors.bar}
        borderLeft="5px solid"
        borderLeftColor={colors.accent}
        px={3}
        py={1.5}
        gap={2}
      >
        <Text fontWeight="800" fontSize="sm" color="#1B2A34" flexShrink={0}>
          {question.id || "Question"}
        </Text>
        <Text fontSize="xs" fontStyle="italic" color="#7B8790" flex="1">
          Written Answer
        </Text>
        <Text fontWeight="800" fontSize="sm" color={colors.score}>
          {awarded}/{max}
        </Text>
      </Flex>

      {tone === "correct" ? (
        <Box px={3} pt={2}>
          <Text fontSize="sm" fontStyle="italic" color={colors.score}>
            Full marks awarded
          </Text>
          {question.feedback ? (
            <MathRichText mt={1} fontSize="xs" color="#6B7280">
              {question.feedback}
            </MathRichText>
          ) : null}
        </Box>
      ) : (
        <Box px={1} pt={2}>
          {question.correct_answer ? (
            <Box
              bg="#E8F1F6"
              borderLeft="4px solid"
              borderLeftColor="#2C3E50"
              px={3}
              py={2}
              mb={2}
            >
              <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#2C3E50" mb={1}>
                CORRECT ANSWER
              </Text>
              <MathRichText fontSize="sm" color="#1B2A34">
                {question.correct_answer}
              </MathRichText>
            </Box>
          ) : null}
          <Box bg="#FDECEC" borderLeft="4px solid" borderLeftColor="#C0392B" px={3} py={3}>
            <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#C0392B" mb={2}>
              {tone === "incorrect" ? "FEEDBACK" : "WHY MARKS WERE LOST"}
            </Text>
            {question.feedback ? (
              <MathRichText fontSize="sm" color="#1B2A34">
                {question.feedback}
              </MathRichText>
            ) : (
              <Text fontSize="sm" color="#1B2A34" lineHeight="1.8">
                Marks were lost on this question.
              </Text>
            )}
          </Box>
        </Box>
      )}

      {hasCriteriaPayload(question.criteria_checks) ? (
        <Box px={1} pb={1}>
          <CriteriaGrid checks={question.criteria_checks} />
        </Box>
      ) : null}
    </Box>
  );
}

const ExamPageFeedback = ({ pageNumber, questions, contextLabel, width }) => {
  if (!questions?.length) return null;

  return (
    <Box
      mx="auto"
      mt={2}
      border="1px solid"
      borderColor="var(--border-subtle)"
      borderRadius="md"
      overflow="hidden"
      bg="#F7F9FB"
      style={{ width: width || "100%", maxWidth: "100%" }}
    >
      <Flex
        bg="#0E3D59"
        color="white"
        px={4}
        py={3}
        justify="space-between"
        align="flex-start"
        gap={3}
      >
        <Box>
          <Text fontWeight="800" fontSize="md" lineHeight="1.2">
            Feedback - Page {pageNumber}
          </Text>
          <Text fontSize="xs" opacity={0.85} mt={1}>
            {questions.map((q) => q.id).filter(Boolean).join(" · ")}
          </Text>
        </Box>
        {contextLabel ? (
          <Text fontSize="xs" opacity={0.85} textAlign="right" maxW="45%">
            {contextLabel}
          </Text>
        ) : null}
      </Flex>
      <Box px={3} py={3}>
        {questions.map((question, index) => (
          <FeedbackQuestionCard
            key={`${question.id || "q"}-${index}-${question.marks_awarded}-${question.feedback || ""}`}
            question={question}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ExamPageFeedback;
